'use client';

import { MessageCircle, UserRound, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  readWikiUser,
  WIKI_AUTH_EVENT,
  writeWikiUser,
  type WikiUser,
} from '../lib/wiki-auth';
import CommentLogin from './comment-login';
import MessageCenter from './message-center';
import SharedComments from './shared-comments';
import { Sheet, SheetClose, SheetContent, SheetTitle } from './ui/sheet';
import './hero-template.css';
import './platform-engagement.css';

export default function PlatformEngagement({
  linkUrl,
  subject,
  title,
  maxWidth = 1080,
  visible = true,
  floatingActions,
}: {
  linkUrl: string;
  subject: string;
  title: string;
  maxWidth?: number;
  visible?: boolean;
  floatingActions?: ReactNode;
}) {
  const commentsRef = useRef<HTMLElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const commentButtonRef = useRef<HTMLButtonElement>(null);
  const [user, setUser] = useState<WikiUser | null>(null);
  const [commentsVisible, setCommentsVisible] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    const syncLogin = () => setUser(readWikiUser());
    syncLogin();
    window.addEventListener('storage', syncLogin);
    window.addEventListener(WIKI_AUTH_EVENT, syncLogin);
    return () => {
      window.removeEventListener('storage', syncLogin);
      window.removeEventListener(WIKI_AUTH_EVENT, syncLogin);
    };
  }, []);

  const openProfile = () => {
    const nextUser = readWikiUser();
    if (nextUser) {
      setUser(nextUser);
      setMessagesOpen(true);
    } else {
      setLoginOpen(true);
    }
  };

  useEffect(() => {
    const section = commentsRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => setCommentsVisible(entry.isIntersecting),
      { threshold: 0.08 },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <SharedComments
        linkUrl={linkUrl}
        subject={subject}
        title={title}
        maxWidth={maxWidth}
        sectionRef={commentsRef}
      />
      <Sheet open={commentsOpen} onOpenChange={setCommentsOpen}>
        <SheetContent
          className="hero-comment-drawer"
          side="right"
          showCloseButton={false}
          finalFocus={commentButtonRef}
        >
          <div className="hero-comment-drawer-head">
            <SheetTitle>评论 · {title}</SheetTitle>
            <SheetClose
              className="hero-comment-drawer-close"
              aria-label="关闭评论抽屉"
            >
              <X />
            </SheetClose>
          </div>
          <div className="hero-comment-drawer-scroll">
            {commentsOpen ? (
              <SharedComments
                linkUrl={linkUrl}
                subject={subject}
                title={title}
                maxWidth={maxWidth}
                compact
              />
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
      <MessageCenter
        open={messagesOpen}
        onOpenChange={setMessagesOpen}
        user={user}
      />
      <CommentLogin
        open={loginOpen}
        onOpenChange={setLoginOpen}
        onLogin={(next) => {
          writeWikiUser(next);
          setUser(next);
          setLoginOpen(false);
          setMessagesOpen(true);
        }}
      />
      {visible && <div className="hero-community-entries platform-community-entries">
        {floatingActions}
        <button
          ref={profileButtonRef}
          className="hero-comment-entry hero-profile-entry"
          type="button"
          title="消息中心"
          onClick={openProfile}
          aria-label="打开消息中心"
          aria-haspopup="dialog"
          aria-expanded={messagesOpen || loginOpen}
        >
          <UserRound />
          {user && (
            <i className="hero-profile-online" aria-label="已登录" />
          )}
        </button>
        {!commentsVisible && (
          <button
            ref={commentButtonRef}
            className="hero-comment-entry"
            type="button"
            title="评论"
            onClick={() => setCommentsOpen(true)}
            aria-label={`打开《${title}》评论抽屉`}
            aria-haspopup="dialog"
            aria-expanded={commentsOpen}
          >
            <MessageCircle />
          </button>
        )}
      </div>}
    </>
  );
}
